import type { Rng } from '../../engine/rng';
import { effectiveness } from '../data/risonanze';
import { getMove, type MoveDef } from '../data/moves';
import { getSpecies } from '../data/species';
import {
  grantXp,
  isFainted,
  maxHp,
  statsFor,
  type LumineInstance,
} from '../lumine';

/**
 * Motore di battaglia a turni, completamente headless: nessun riferimento a
 * render o DOM. La UI consuma la lista di BattleEvent prodotta da ogni turno.
 * In Fase 2 lo stesso motore potrà girare sul server (battaglie PvP) inviando
 * gli eventi via NetworkAdapter.
 */

export type PlayerAction =
  | { kind: 'move'; moveIndex: number }
  | { kind: 'capture' }
  | { kind: 'switch'; teamIndex: number }
  | { kind: 'flee' };

export type BattleEvent =
  | { kind: 'message'; text: string }
  | { kind: 'damage'; target: 'player' | 'enemy'; amount: number; effectiveness: number }
  | { kind: 'faint'; target: 'player' | 'enemy' }
  | { kind: 'captureAttempt'; success: boolean }
  | { kind: 'xp'; amount: number; leveledUp: boolean; newLevel: number }
  | { kind: 'switch'; teamIndex: number }
  | { kind: 'fled'; success: boolean }
  | { kind: 'end'; outcome: BattleOutcome };

export type BattleOutcome = 'victory' | 'defeat' | 'captured' | 'fled';

export class BattleEngine {
  activeIndex = 0;
  outcome: BattleOutcome | null = null;

  constructor(
    public readonly team: LumineInstance[],
    public readonly enemy: LumineInstance,
    private rng: Rng,
  ) {
    const firstAble = team.findIndex((l) => !isFainted(l));
    if (firstAble === -1) throw new Error('La squadra non ha Lumini in grado di lottare');
    this.activeIndex = firstAble;
  }

  get active(): LumineInstance {
    return this.team[this.activeIndex]!;
  }

  /** Risolve un intero turno a partire dall'azione del giocatore. */
  takeTurn(action: PlayerAction): BattleEvent[] {
    if (this.outcome) return [];
    const events: BattleEvent[] = [];

    switch (action.kind) {
      case 'flee': {
        const playerSpd = statsFor(getSpecies(this.active.speciesId), this.active.level).spd;
        const enemySpd = statsFor(getSpecies(this.enemy.speciesId), this.enemy.level).spd;
        const chance = Math.min(0.95, 0.5 + (playerSpd - enemySpd) * 0.04);
        if (this.rng.chance(chance)) {
          events.push({ kind: 'fled', success: true });
          this.finish('fled', events);
          return events;
        }
        events.push({ kind: 'fled', success: false });
        this.enemyTurn(events);
        return events;
      }
      case 'capture': {
        const ratio = this.enemy.hp / maxHp(this.enemy);
        const chance = Math.max(0.08, Math.min(0.92, 0.3 + 0.6 * (1 - ratio)));
        const success = this.rng.chance(chance);
        events.push({ kind: 'captureAttempt', success });
        if (success) {
          this.finish('captured', events);
          return events;
        }
        this.enemyTurn(events);
        return events;
      }
      case 'switch': {
        const target = this.team[action.teamIndex];
        if (!target || isFainted(target) || action.teamIndex === this.activeIndex) {
          events.push({ kind: 'message', text: 'Non puoi schierare questo Lumine.' });
          return events;
        }
        this.activeIndex = action.teamIndex;
        events.push({ kind: 'switch', teamIndex: action.teamIndex });
        this.enemyTurn(events);
        return events;
      }
      case 'move': {
        const moveId = this.active.moves[action.moveIndex];
        if (!moveId) {
          events.push({ kind: 'message', text: 'Mossa non disponibile.' });
          return events;
        }
        const playerMove = getMove(moveId);
        const playerSpd = statsFor(getSpecies(this.active.speciesId), this.active.level).spd;
        const enemySpd = statsFor(getSpecies(this.enemy.speciesId), this.enemy.level).spd;
        const playerFirst = playerSpd === enemySpd ? this.rng.chance(0.5) : playerSpd > enemySpd;

        if (playerFirst) {
          this.playerAttack(playerMove, events);
          if (!this.outcome && !isFainted(this.enemy)) this.enemyTurn(events);
        } else {
          this.enemyTurn(events);
          if (!this.outcome && !isFainted(this.active)) this.playerAttack(playerMove, events);
        }
        return events;
      }
    }
  }

  private playerAttack(move: MoveDef, events: BattleEvent[]): void {
    this.resolveAttack(this.active, this.enemy, move, 'enemy', events);
    if (isFainted(this.enemy)) {
      events.push({ kind: 'faint', target: 'enemy' });
      const xp = this.enemy.level * 14;
      const result = grantXp(this.active, xp);
      events.push({
        kind: 'xp',
        amount: xp,
        leveledUp: result.leveledUp,
        newLevel: result.newLevel,
      });
      this.finish('victory', events);
    }
  }

  private enemyTurn(events: BattleEvent[]): void {
    if (this.outcome || isFainted(this.enemy)) return;
    const moveId = this.rng.pick(this.enemy.moves);
    const move = getMove(moveId);
    this.resolveAttack(this.enemy, this.active, move, 'player', events);
    if (isFainted(this.active)) {
      events.push({ kind: 'faint', target: 'player' });
      const nextIndex = this.team.findIndex((l) => !isFainted(l));
      if (nextIndex === -1) {
        this.finish('defeat', events);
      } else {
        events.push({
          kind: 'message',
          text: 'Scegli un altro Lumine da schierare!',
        });
      }
    }
  }

  private resolveAttack(
    attacker: LumineInstance,
    defender: LumineInstance,
    move: MoveDef,
    targetLabel: 'player' | 'enemy',
    events: BattleEvent[],
  ): void {
    const attackerSpecies = getSpecies(attacker.speciesId);
    events.push({ kind: 'message', text: `${attackerSpecies.name} usa ${move.name}!` });
    if (!this.rng.chance(move.accuracy)) {
      events.push({ kind: 'message', text: 'Il colpo va a vuoto!' });
      return;
    }
    const damage = computeDamage(attacker, defender, move, this.rng);
    defender.hp = Math.max(0, defender.hp - damage.amount);
    events.push({
      kind: 'damage',
      target: targetLabel,
      amount: damage.amount,
      effectiveness: damage.effectiveness,
    });
  }

  /** Schiera un sostituto dopo un KO (non consuma il turno nemico). */
  replaceFainted(teamIndex: number): BattleEvent[] {
    const target = this.team[teamIndex];
    if (!target || isFainted(target)) {
      return [{ kind: 'message', text: 'Questo Lumine non può lottare.' }];
    }
    this.activeIndex = teamIndex;
    return [{ kind: 'switch', teamIndex }];
  }

  private finish(outcome: BattleOutcome, events: BattleEvent[]): void {
    this.outcome = outcome;
    events.push({ kind: 'end', outcome });
  }
}

export interface DamageResult {
  amount: number;
  effectiveness: number;
}

export function computeDamage(
  attacker: LumineInstance,
  defender: LumineInstance,
  move: MoveDef,
  rng: Rng,
): DamageResult {
  const atkStats = statsFor(getSpecies(attacker.speciesId), attacker.level);
  const defStats = statsFor(getSpecies(defender.speciesId), defender.level);
  const eff = effectiveness(move.risonanza, getSpecies(defender.speciesId).risonanza);
  // STAB ("sintonia"): bonus se la mossa condivide la Risonanza della specie.
  const stab = getSpecies(attacker.speciesId).risonanza === move.risonanza ? 1.25 : 1;
  const variance = 0.85 + rng.next() * 0.15;
  const base = ((2 * attacker.level) / 5 + 2) * move.power * (atkStats.atk / defStats.def);
  const amount = Math.max(1, Math.floor((base / 50 + 2) * eff * stab * variance));
  return { amount, effectiveness: eff };
}
